import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/JoinPage.css';
import { createJoinRequest } from '../services/joinRequests';

const JoinPage = () => {
  const [showApplication, setShowApplication] = useState(false);
  const [formData, setFormData] = useState({
    riderName: '',
    riderPhone: '',
    riderAge: '',
    riderExperience: '',
    hasMotorcycle: 'no',
    licenseNumber: '',
    availability: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // التحقق من البيانات
      if (parseInt(formData.riderAge) < 21) {
        throw new Error('يجب أن يكون عمرك 21 عاماً على الأقل');
      }
      if (parseInt(formData.riderExperience) < 2) {
        throw new Error('يجب أن تمتلك خبرة لا تقل عن سنتين');
      }
      if (!/^01[0125][0-9]{8}$/.test(formData.riderPhone)) {
        throw new Error('يرجى إدخال رقم هاتف صحيح');
      }

      const result = await createJoinRequest(formData);
      if (result.success) {
        setSuccess(true);
        // إعادة تعيين النموذج
        setFormData({
          riderName: '',
          riderPhone: '',
          riderAge: '',
          riderExperience: '',
          hasMotorcycle: 'no',
          licenseNumber: '',
          availability: ''
        });
        setShowApplication(false);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setError(error.message || 'حدث خطأ أثناء إرسال الطلب. الرجاء المحاولة مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": "سائق توصيل - كابتن زد",
    "description": "نبحث عن سائقين محترفين للانضمام إلى فريق زد للتوصيل في الإسماعيلية. يجب أن يكون لديك خبرة لا تقل عن سنتين في القيادة وأن يكون عمرك 21 عاماً على الأقل.",
    "datePosted": new Date().toISOString(),
    "validThrough": new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString(),
    "employmentType": "CONTRACTOR",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "ZED Delivery Service",
      "sameAs": "https://zedorder.com"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ismailia",
        "addressRegion": "Ismailia Governorate",
        "addressCountry": "EG"
      }
    },
    "baseSalary": {
      "@type": "MonetaryAmount",
      "currency": "EGP",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": 4000,
        "maxValue": 8000,
        "unitText": "MONTH"
      }
    },
    "qualifications": "رخصة قيادة دراجة نارية سارية، خبرة سنتين على الأقل",
    "responsibilities": "توصيل الطلبات للعملاء، التعامل باحترافية، الالتزام بمواعيد التسليم",
    "skills": "قيادة الدراجات النارية، معرفة جيدة بطرق الإسماعيلية، مهارات تواصل جيدة"
  };

  return (
    <div className="join-page">
      <Helmet>
        <title>انضم إلى فريق زد للتوصيل | وظائف سائقين في الإسماعيلية</title>
        <meta name="description" content="انضم إلى فريق زد للتوصيل في الإسماعيلية. نبحث عن سائقين محترفين للعمل معنا. رواتب مجزية، ساعات عمل مرنة، وفرص نمو مهني." />
        <meta name="keywords" content="وظائف توصيل, كابتن توصيل, سائق دراجة نارية, وظائف الإسماعيلية, زد للتوصيل, delivery jobs, كابتن ديلفري" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="وظائف توصيل في الإسماعيلية | انضم إلى فريق زد" />
        <meta property="og:description" content="فرص عمل متاحة للسائقين في زد للتوصيل. رواتب مجزية وساعات عمل مرنة. قدم طلبك الآن!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zedorder.com/join" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="وظائف توصيل في الإسماعيلية | زد للتوصيل" />
        <meta name="twitter:description" content="انضم إلى فريق زد للتوصيل. نبحث عن سائقين محترفين في الإسماعيلية." />
        
        {/* Schema.org job posting markup */}
        <script type="application/ld+json">
          {JSON.stringify(jobPostingSchema)}
        </script>
      </Helmet>
      <div className="container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        {success && (
          <div className="success-message">
            تم إرسال طلبك بنجاح! سنتواصل معك قريباً.
          </div>
        )}
        {/* Instructions Section */}
        <div className="join-instructions" id="instructionsSection" style={{ display: !showApplication ? 'block' : 'none' }}>
          <h2 className="instructions-title">انضم إلى فريق التوصيل</h2>
          <div className="instructions-content">
            <ol className="instructions-list">
              <li>
                <span className="instruction-number">1</span>
                يجب أن لا يقل العمر عن 18 عاماً
              </li>
              <li>
                <span className="instruction-number">2</span>
                رخصة قيادة دراجة نارية سارية المفعول
              </li>
              <li>
                <span className="instruction-number">3</span>
                خبرة لا تقل عن سنتين في القيادة
              </li>
              <li>
                <span className="instruction-number">4</span>
                يفضل امتلاك دراجة نارية (غير إلزامي)
              </li>
              <li>
                <span className="instruction-number">5</span>
                ساعات عمل مرنة متاحة
              </li>
            </ol>

            <div className="checkbox-container">
              <input
                type="checkbox"
                id="instructionsRead"
                onChange={() => setShowApplication(true)}
              />
              <label htmlFor="instructionsRead">
                لقد قرأت وفهمت جميع المتطلبات
              </label>
            </div>
          </div>
        </div>

        {/* Application Form */}
        <div className="join-instructions" id="applicationSection" style={{ display: showApplication ? 'block' : 'none' }}>
          <h2 className="instructions-title">نموذج طلب الانضمام</h2>
          <form id="joinForm" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="riderName">الاسم بالكامل</label>
                <input
                  type="text"
                  id="riderName"
                  name="riderName"
                  value={formData.riderName}
                  onChange={handleInputChange}
                  placeholder="أدخل اسمك بالكامل"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="riderPhone">رقم الهاتف</label>
                <input
                  type="tel"
                  id="riderPhone"
                  name="riderPhone"
                  value={formData.riderPhone}
                  onChange={handleInputChange}
                  placeholder="أدخل رقم هاتفك"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="riderAge">العمر</label>
                <input
                  type="number"
                  id="riderAge"
                  name="riderAge"
                  value={formData.riderAge}
                  onChange={handleInputChange}
                  min="21"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="riderExperience">سنوات الخبرة في القيادة</label>
                <input
                  type="number"
                  id="riderExperience"
                  name="riderExperience"
                  value={formData.riderExperience}
                  onChange={handleInputChange}
                  min="2"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="hasMotorcycle">هل تمتلك دراجة نارية؟</label>
              <select
                id="hasMotorcycle"
                name="hasMotorcycle"
                value={formData.hasMotorcycle}
                onChange={handleInputChange}
                required
              >
                <option value="yes">نعم</option>
                <option value="no">لا</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="licenseNumber">رقم الرخصة</label>
              <input
                type="text"
                id="licenseNumber"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
                placeholder="أدخل رقم رخصة القيادة"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="availability">ساعات العمل المفضلة</label>
              <textarea
                id="availability"
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                placeholder="صف ساعات عملك المفضلة"
                rows="3"
                required
              ></textarea>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowApplication(false)}>
                العودة للتعليمات
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
